import React from "react"
import { Navigate, Outlet, Route, Routes } from "react-router-dom"

import { Footer, Header } from "~/components/organisms"

import { LoadingOverlay } from "./components/molecules"
import CreateReferralComments from "./components/templates/CreateReferralComments"
import Interview from "./components/templates/interviews/v2/Interview"
import IntroduceCampaign from "./components/templates/IntroduceCampaign"
import MemberDetail from "./components/templates/MemberDetail"
import ProductDetail from "./components/templates/ProductDetail"
import Settings from "./components/templates/Settings"
import { useUser } from "./containers"
import CampaignProvider from "./containers/CampaignProvider"
import UnreadMessagesProvider from "./containers/UnreadMessagesProvider"

const CompanyDetails = React.lazy(() => import("./components/templates/CompanyDetails"))
const CreateProfile = React.lazy(() => import("./components/templates/CreateProfile"))
const EditProfile = React.lazy(() => import("./components/templates/EditProfile"))
const JobDetails = React.lazy(() => import("./components/templates/JobDetails"))
const Likes = React.lazy(() => import("./components/templates/Likes"))
const Messaging = React.lazy(() => import("./components/templates/Messaging"))
const ProfileTips = React.lazy(() => import("./components/templates/ProfileTips"))
const Contents = React.lazy(() => import("./components/templates/Contents"))
const Top = React.lazy(() => import("./components/templates/top/Top"))
const TopAd = React.lazy(() => import("./components/templates/top/TopAd"))
const FeaturedJobs = React.lazy(() => import("./components/templates/FeaturedJobs"))
const FeatureTopics = React.lazy(() => import("./components/templates/FeatureTopics"))
const Rikelink = React.lazy(() => import("./components/templates/Rikelink"))

const Private: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { user, loading } = useUser()
  if (loading) return <LoadingOverlay />

  return user ? (
    <UnreadMessagesProvider>
      <CampaignProvider>{children}</CampaignProvider>
    </UnreadMessagesProvider>
  ) : (
    <Navigate to="/" />
  )
}

const Layout = () => (
  <>
    <Header />
    <Outlet />
    <Footer />
  </>
)

const AppRoutes = (): JSX.Element => (
  <React.Suspense fallback={<LoadingOverlay />}>
    <Routes>
      <Route element={<Layout />}>
        <Route path="/">
          <Route index element={<Top variant="normal" />} />
          <Route path="ad" element={<Top variant="normal" />} />
          <Route path="ad_form" element={<TopAd />} />
          <Route path="information" element={<Top variant="normal" />} />
          <Route path="student/basic/">
            <Route path="*" element={<Top variant="intern" />} />
          </Route>
          <Route path="mendan" element={<Top variant="interview" />} />
          {/* <Route path="jobs" element={Home} /> */}
          <Route
            path="likes"
            element={
              <Private>
                <Likes />
              </Private>
            }
          />
          <Route path="jobs/:id" element={<JobDetails />} />
          <Route path="company/:id" element={<CompanyDetails />} />
          <Route path="/topics" element={<FeatureTopics />} />
          <Route path="topics/:slug" element={<FeaturedJobs />} />
          <Route path="/members/:id" element={<MemberDetail />} />
          <Route path="/products/:id" element={<ProductDetail />} />

          <Route
            path="messages"
            element={
              <Private>
                <Messaging />
              </Private>
            }
          />
          <Route
            path="messages/:threadId"
            element={
              <Private>
                <Messaging />
              </Private>
            }
          />
          <Route path="profile">
            <Route
              index
              element={
                <Private>
                  <EditProfile />
                </Private>
              }
            />
            <Route
              path="new"
              element={
                <Private>
                  <CreateProfile />
                </Private>
              }
            />
            <Route
              path="tips"
              element={
                <Private>
                  <ProfileTips />
                </Private>
              }
            />
          </Route>

          <Route
            path="settings"
            element={
              <Private>
                <Settings />
              </Private>
            }
          />

          <Route
            path="rikelink"
            element={
              <Private>
                <Rikelink />
              </Private>
            }
          />

          <Route
            path="contents"
            element={
              <Private>
                <Contents />
              </Private>
            }
          />

          <Route
            path="campaign"
            element={
              <Private>
                <IntroduceCampaign />
              </Private>
            }
          />

          <Route
            path="interview"
            element={
              <Private>
                <Interview />
              </Private>
            }
          />

          {/* fallback */}
          <Route path="*" element={<Navigate to="/" />} />
        </Route>
      </Route>
      <Route path="rikelink/:slug" element={<Rikelink />} />
      <Route path="rikelink/:slug/referral_comments/new" element={<CreateReferralComments />} />
    </Routes>
  </React.Suspense>
)

export default AppRoutes
